import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";
import moment from "moment";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import subscriptionsService from "../../services/api/subscription-membershipl-service";
import projectsService from "../../services/api/projects-service";
import projectUsersService from "../../services/api/project-users-service";

const loadProjectOptions = async (inputValue) => {
  const filters = { projectname: inputValue || "", status: 1 };
  const projectsData = await projectsService.getAllProjects(filters, 1, 30);
  if (!projectsData.status || !Array.isArray(projectsData.data)) return [];
  return projectsData.data.map((project) => ({
    value: project.id || project._id,
    label: `${project.name || "(no name)"} — ${project.slug || ""}`,
  }));
};

const debouncedFetchUserOptions = debounce(async (inputValue, callback, projectId) => {
  try {
    const users = await projectUsersService.fetchUsers({
      project_id: projectId,
      search: inputValue,
      page: 1,
      page_size: "",
    });

    if (users?.status && users?.data?.users) {
      callback(
        users.data.users.map((user) => ({
          value: user.user_id,
          label: user.email
            ? `${user.name} (${user.email})`
            : `${user.name} ${user.phone_number}`,
        }))
      );
    } else {
      callback([]);
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    callback([]);
  }
}, 500);

const CreateComplementary = () => {
  const [project, setProject] = useState(null);
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [activePlan, setActivePlan] = useState(null);
  const [checkingUser, setCheckingUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchPlans = async () => {
      setPlans([]);
      setPlan(null);
      if (!project) return;
      try {
        const result = await subscriptionsService.getProjectPlans(project.value);
        if (result?.status && Array.isArray(result.data)) {
          setPlans(result.data);
        }
      } catch (err) {
        console.error("Error fetching plans:", err);
      }
    };

    fetchPlans();
  }, [project]);

  useEffect(() => {
    const checkUser = async () => {
      setActivePlan(null);
      if (!project || !user) return;
      setCheckingUser(true);
      try {
        const result = await subscriptionsService.getUserActivePlan(project.value, user.value);
        if (result?.status) {
          setActivePlan(result.data?.active_plan || null);
        }
      } catch (err) {
        console.error("Error checking user plan:", err);
      } finally {
        setCheckingUser(false);
      }
    };

    checkUser();
  }, [project, user]);

  useEffect(() => {
    if (!plan || !startDate) return;
    setEndDate(moment(startDate).add(plan.months, "months").format("YYYY-MM-DD"));
  }, [plan, startDate]);

  const fetchUserOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (!project) return resolve([]);
        debouncedFetchUserOptions(inputValue, resolve, project.value);
      }),
    [project]
  );

  const planOptions = plans.map((item) => ({
    ...item,
    value: item.plan_id,
    label: `${item.display_name} — ${item.currency} ${item.amount} (${item.period}, ${item.tier})`,
  }));

  const resetForm = () => {
    setUser(null);
    setPlan(null);
    setStartDate(moment().format("YYYY-MM-DD"));
    setEndDate("");
    setReason("");
    setActivePlan(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!project || !user || !plan) {
      setError("Project, user and plan are required.");
      return;
    }
    if (!reason.trim()) {
      setError("Reason is required.");
      return;
    }
    if (!endDate || moment(endDate).isSameOrBefore(moment(startDate))) {
      setError("End date must be after start date.");
      return;
    }

    setSaving(true);
    try {
      const result = await subscriptionsService.createComplementaryMembership({
        project_id: project.value,
        user_id: user.value,
        plan_id: plan.value,
        start_date: startDate,
        end_date: endDate,
        reason,
      });

      if (result?.status) {
        setSuccess(result.data?.message || "Complementary membership assigned successfully");
        resetForm();
      } else {
        setError(result?.error?.message || "Failed to assign complementary membership.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.error?.message || "Failed to assign complementary membership."
      );
    } finally {
      setSaving(false);
    }
  };

  const isBlocked = !!activePlan;

  return (
    <div className="content">
      <Container>
        <Row>
          <Col className="ml-auto mr-auto" md="12">
            <Card className="card-signup">
              <CardHeader className="text-center">
                <CardTitle tag="h4">Assign Complementary Membership</CardTitle>
                <p className="card-category text-muted">
                  Give a free membership to a user. Not allowed if the user already has an
                  active plan.
                </p>
              </CardHeader>
              <CardBody>
                <Form className="form-horizontol" onSubmit={handleSubmit}>
                  <Row>
                    <Label className="bold" sm="3">
                      Select Project
                    </Label>
                    <Col sm="6">
                      <FormGroup>
                        <AsyncSelect
                          className="react-select primary"
                          classNamePrefix="react-select"
                          isClearable
                          placeholder="Search project..."
                          defaultOptions
                          loadOptions={loadProjectOptions}
                          value={project}
                          onChange={(option) => {
                            setProject(option);
                            setUser(null);
                            setActivePlan(null);
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Label className="bold" sm="3">
                      Select User
                    </Label>
                    <Col sm="6">
                      <FormGroup>
                        <AsyncSelect
                          className="react-select primary"
                          classNamePrefix="react-select"
                          isClearable
                          isDisabled={!project}
                          placeholder="Search user by name, email or phone..."
                          defaultOptions={false}
                          loadOptions={fetchUserOptions}
                          value={user}
                          onChange={(option) => setUser(option)}
                          noOptionsMessage={({ inputValue }) =>
                            inputValue ? "No users found" : "Search to get options"
                          }
                        />
                        {checkingUser && (
                          <small className="text-muted">
                            Checking existing membership...
                          </small>
                        )}
                        {isBlocked && (
                          <label className="error d-block mt-1">
                            User already has an active plan <b>{activePlan.plan_id}</b> (
                            {activePlan.status}) till{" "}
                            {moment.unix(activePlan.current_end).format("DD MMM YYYY")}.
                          </label>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Label className="bold" sm="3">
                      Select Plan
                    </Label>
                    <Col sm="6">
                      <FormGroup>
                        <Select
                          className="react-select primary"
                          classNamePrefix="react-select"
                          isClearable
                          isDisabled={!project || !plans.length}
                          placeholder={plans.length ? "Select plan" : "No plans configured"}
                          options={planOptions}
                          value={plan}
                          onChange={(option) => setPlan(option)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Label className="bold" sm="3">
                      Start Date
                    </Label>
                    <Col sm="6">
                      <FormGroup>
                        <Input
                          type="date"
                          value={startDate}
                          onChange={(event) => setStartDate(event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Label className="bold" sm="3">
                      End Date
                    </Label>
                    <Col sm="6">
                      <FormGroup>
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(event) => setEndDate(event.target.value)}
                        />
                        <small className="text-muted">
                          Defaults to the plan duration, can be changed.
                        </small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Label className="bold" sm="3">
                      Reason <span className="text-danger">*</span>
                    </Label>
                    <Col sm="6">
                      <FormGroup>
                        <Input
                          type="textarea"
                          rows="2"
                          value={reason}
                          placeholder="Why is this membership being given?"
                          onChange={(event) => setReason(event.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm={{ size: 6, offset: 3 }}>
                      {error && <label className="error d-block mb-2">{error}</label>}
                      {success && <label className="text-success d-block mb-2">{success}</label>}
                      <Button
                        color="primary"
                        type="submit"
                        disabled={saving || isBlocked || !reason.trim()}
                      >
                        {saving ? <Spinner size="sm" /> : "Assign Membership"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CreateComplementary;
